// Import React hooks and GSAP animation library
import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger); // load scroll trigger plugin

const AimAnimatedNumber = ({
  value,
  duration = 2,
  suffix = '',
  delay = 0,
  ease = 'power2.out', // Animation smoothness style
  triggerElement = null // What element to watch for scroll (container or self)
}) => {
  const numberRef = useRef(null);
  const selfRef = useRef(null);

  useGSAP(() => {
    if (!numberRef.current || !selfRef.current) return;

    // Add a small delay to ensure ScrollSmoother is initialized
    const timer = setTimeout(() => {
      // Create object to animate - GSAP will change this value from 0 to target
      const counter = { value: 0 };

      const trigger = triggerElement?.current || selfRef.current;

      if (!trigger) {
        console.log('AimAnimatedNumber: No trigger element found');
        return;
      }

      // STEP 1: Hide the number initially (before user scrolls to it)
      gsap.set(selfRef.current, {
        opacity: 0, // Invisible
        scale: 0.8, // Smaller than final size
        y: 20 // Slightly below final position
      });

      // STEP 2: Create animation sequence that triggers on scroll
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: trigger, // Element to watch
          start: triggerElement ? 'top 85%' : 'top bottom-=100px',
          once: true // Only animate once
        }
      });

      // STEP 3: Pop-up animation - make visible with bounce effect
      tl.to(selfRef.current, {
        opacity: 1, // Fade in
        scale: 1, // Grow to full size
        y: 0,
        duration: 0.6,
        ease: 'back.out(1.7)', // Bouncy effect
        delay: delay // Wait for stagger
      });

      // STEP 4: Number counting animation (starts slightly after pop-up)
      tl.to(
        counter,
        {
          value: value, // Count from 0 to final number
          duration: duration, // How long counting takes
          ease: ease, // Smoothness of counting
          onUpdate: () => {
            if (numberRef.current) {
              const currentValue = Math.floor(counter.value);
              numberRef.current.textContent = currentValue.toLocaleString();
            }
          }
        },
        delay + 0.2
      ); // Start counting 0.2 seconds after pop-up begins
    }, 200); // Wait 200ms for ScrollSmoother to initialize

    // Cleanup
    return () => {
      clearTimeout(timer);
    };
  }, [value, duration, delay, ease, triggerElement]);

  return (
    <div
      ref={selfRef}
      className="w-16 md:w-auto text-2xl md:text-4xl text-secondaryAim flex justify-center"
    >
      <span ref={numberRef} className="mr-1">
        0
      </span>
      <span className="">{suffix}</span>
    </div>
  );
};

export default AimAnimatedNumber;
